import React, { useState } from "react";
import { DownloadSimple, Info } from "@phosphor-icons/react";
import { useEinstufungProgress } from "../context/EinstufungProgressContext";
import { formatYearsMonths, formatDurationDays } from "../pages/tool/shared";
import KvDetailsSheet from "./KvDetailsSheet";

function formatGermanDate(iso) {
  if (!iso) return "—";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** Strip the long parenthetical suffix from a KV name so the card stays compact.
 *  Example: "KV Handel (Angestellte und Lehrlinge in Handelsbetrieben)" → "KV Handel". */
function shortKvName(kuerzel, fullName) {
  if (kuerzel && kuerzel.trim()) return kuerzel.trim();
  if (!fullName) return "—";
  const idx = fullName.indexOf("(");
  return (idx > 0 ? fullName.slice(0, idx) : fullName).trim();
}

function ProgressCard({
  num,
  label,
  value,
  subtitle,
  highlight,
  onPdfClick,
  pdfBusy,
  testId,
  delayMs = 0,
  stale = false,
  onInfoClick,
  infoTitle,
  infoTestId,
}) {
  return (
    <div
      data-testid={testId}
      className={[
        "relative rounded-sm border p-3 wizard-card-in",
        stale
          ? "border-status-ausstehend-border bg-status-ausstehend-bg"
          : highlight
            ? "border-brand-400 bg-brand-50/40 shadow-[0_1px_0_rgba(255,150,0,0.15)]"
            : "border-ink-200 bg-white",
      ].join(" ")}
      style={{ animationDelay: `${delayMs}ms` }}
    >
      <span
        aria-hidden="true"
        className={`absolute top-0 left-0 h-0.5 w-10 ${stale ? "bg-status-ausstehend-text" : "bg-brand-400"}`}
      />
      <div className="flex items-center justify-between gap-2">
        <div className="text-[9px] uppercase tracking-widest font-semibold text-warm-500 mb-1">
          {num} · {label}
        </div>
        {onInfoClick && (
          <button
            type="button"
            onClick={onInfoClick}
            aria-label={infoTitle || "Details anzeigen"}
            title={infoTitle || "Details anzeigen"}
            data-testid={infoTestId || `${testId}-info`}
            className="-mt-1 -mr-1 inline-flex h-7 w-7 items-center justify-center rounded-full border border-brand-300 bg-brand-50 text-brand-600 hover:bg-brand-100 hover:text-brand-700 hover:border-brand-400 transition-crisp focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1"
          >
            <Info size={18} weight="bold" />
          </button>
        )}
      </div>
      <div
        className={[
          "font-cabinet font-medium tabular-nums leading-tight break-words",
          stale
            ? "text-sm text-status-ausstehend-text italic"
            : highlight
              ? "text-2xl text-brand-600"
              : "text-sm text-ink-900 line-clamp-2",
        ].join(" ")}
      >
        {value}
      </div>
      {subtitle && (
        <div className="text-[11px] text-warm-600 mt-1 leading-snug">
          {subtitle}
        </div>
      )}
      {onPdfClick && (
        <button
          type="button"
          onClick={onPdfClick}
          disabled={pdfBusy}
          data-testid="wizard-live-pdf-download"
          className="mt-2 inline-flex items-center gap-1.5 rounded-sm border border-brand-400 bg-white px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-brand-700 transition-crisp hover:bg-brand-50 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          <DownloadSimple size={11} weight="bold" />
          {pdfBusy ? "Wird erstellt…" : "PDF herunterladen"}
        </button>
      )}
    </div>
  );
}

/**
 * Sidebar "Wizard-Live-View" — mirrors the 4-step Stepper at the top of
 * the tool page. Progressive Disclosure: cards only mount once their
 * value is set; nothing is shown on a fresh /dashboard.
 *
 *   01 · KOLLEKTIVVERTRAG   primary: KV-Kurzname   subtitle: "Stichtag: …"
 *   02 · TÄTIGKEIT          primary: "Gruppe X"    (KI-Vorschlag aus Step 2)
 *   03 · VORDIENSTZEITEN    primary: "7 J. 0 M."   (anrechenbar, gecappt+overlap-resolved)
 *   04 · EMPFEHLUNG         primary: "D / 4"       highlight + PDF-Download-Shortcut
 */
export default function WizardLiveView() {
  const { progress, pdfDownload, pdfBusy } = useEinstufungProgress();
  // Iter 19.65.10 — KV-Kernregeln-Sheet
  const [kvSheetOpen, setKvSheetOpen] = useState(false);

  const card01Visible = Boolean(progress.kvName);
  const card02Visible = Boolean(progress.suggestedGroup);
  const card03Visible = progress.anrechenbareMonate != null && progress.anrechenbareMonate > 0;
  const card04Visible = Boolean(progress.finalGroup);

  if (!card01Visible && !card02Visible && !card03Visible && !card04Visible) {
    return null;
  }

  return (
    <div
      data-testid="wizard-live-view"
      className="px-3 pt-3 pb-1 overflow-y-auto"
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 px-1 mb-2">
        Aktuelle Einstufung
      </div>
      <div className="space-y-2.5">
        {card01Visible && (
          <ProgressCard
            testId="wizard-card-kv"
            num="01"
            label="Kollektivvertrag"
            value={shortKvName(progress.kvKuerzel, progress.kvName)}
            subtitle={
              progress.stichtag
                ? `Stichtag: ${formatGermanDate(progress.stichtag)}`
                : null
            }
            delayMs={0}
            onInfoClick={progress.kvId ? () => setKvSheetOpen(true) : null}
            infoTitle="Kernregeln dieses Kollektivvertrags anzeigen"
            infoTestId="wizard-card-kv-info"
          />
        )}
        {card02Visible && (
          <ProgressCard
            testId="wizard-card-taetigkeit"
            num="02"
            label="Tätigkeit"
            value={`Gruppe ${progress.suggestedGroup}`}
            delayMs={40}
          />
        )}
        {card03Visible && (
          <ProgressCard
            testId="wizard-card-vordienst"
            num="03"
            label="Vordienstzeiten"
            value={
              // Iter 19.43.7 — Stale-State: User hat editiert seit letztem KI-Check.
              progress.anrechenbareStale
                ? "Neuberechnung nötig"
                : // Iter 19.42.1 — Bevorzuge das vom Tool berechnete Hybrid-Display.
                  progress.anrechenbareDisplay ||
                  (progress.anrechenbareTage != null
                    ? formatDurationDays(progress.anrechenbareTage)
                    : formatDurationDays((progress.anrechenbareMonate || 0) * 30))
            }
            stale={Boolean(progress.anrechenbareStale)}
            delayMs={80}
          />
        )}
        {card04Visible && (
          <ProgressCard
            testId="wizard-card-empfehlung"
            num="04"
            label="Empfehlung"
            value={
              progress.finalStufe != null && progress.finalStufe !== ""
                ? `${progress.finalGroup} / ${progress.finalStufe}`
                : progress.finalGroup
            }
            highlight
            onPdfClick={pdfDownload || undefined}
            pdfBusy={pdfBusy}
            delayMs={120}
          />
        )}
      </div>
      <KvDetailsSheet
        kvId={progress.kvId}
        kvName={progress.kvName}
        kvKuerzel={progress.kvKuerzel}
        open={kvSheetOpen}
        onClose={() => setKvSheetOpen(false)}
      />
    </div>
  );
}
